import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import PropTypes from "prop-types";

import { getStates } from "../../api/services/StateService";
import { getDistricts } from "../../api/services/DistrictService";

const InstitutionForm = ({
    show,
    onClose,
    onSubmit,
    initialData,
    loading
}) => {
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);

    const [formData, setFormData] = useState({
        name: "",
        stateCode: "",
        districtName: "",
        level: "",
        type: ""
    });

    const [errors, setErrors] = useState({});

    /* -------------------------------------------------------------------------- */
    /* LOAD STATES                                                                */
    /* -------------------------------------------------------------------------- */
    useEffect(() => {
        const loadStates = async () => {
            try {
                const data = await getStates();
                setStates(data || []);
            } catch (err) {
                console.error("Failed to load states:", err);
            }
        };

        if (show) loadStates();
    }, [show]);

    /* -------------------------------------------------------------------------- */
    /* LOAD DISTRICTS (BASED ON STATE)                                             */
    /* -------------------------------------------------------------------------- */
    useEffect(() => {
        const loadDistricts = async () => {
            if (!formData.stateCode) {
                setDistricts([]);
                return;
            }

            try {
                const data = await getDistricts({
                    stateCode: formData.stateCode
                });
                setDistricts(data || []);
            } catch (err) {
                console.error("Failed to load districts:", err);
            }
        };

        loadDistricts();
    }, [formData.stateCode]);

    /* -------------------------------------------------------------------------- */
    /* LOAD DATA FOR EDIT                                                         */
    /* -------------------------------------------------------------------------- */
    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name || "",
                stateCode: initialData.stateCode || "",
                districtName: initialData.districtName || "",
                level: initialData.level || "",
                type: initialData.type || ""
            });
        } else {
            setFormData({
                name: "",
                stateCode: "",
                districtName: "",
                level: "",
                type: ""
            });
        }
    }, [initialData]);

    /* -------------------------------------------------------------------------- */
    /* VALIDATION                                                                 */
    /* -------------------------------------------------------------------------- */
    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim())
            newErrors.name = "Institution name is required";

        if (!formData.stateCode)
            newErrors.stateCode = "State is required";

        if (!formData.districtName)
            newErrors.districtName = "District is required";

        if (!formData.level)
            newErrors.level = "Level is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* -------------------------------------------------------------------------- */
    /* CHANGE HANDLER                                                             */
    /* -------------------------------------------------------------------------- */
    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

        if (name === "stateCode") {
            setFormData((prev) => ({
                ...prev,
                districtName: ""
            }));
        }
    };

    /* -------------------------------------------------------------------------- */
    /* SUBMIT HANDLER                                                             */
    /* -------------------------------------------------------------------------- */
    const handleSubmit = () => {
        if (!validate()) return;

        onSubmit({
            name: formData.name.trim(),
            stateCode: formData.stateCode,
            districtName: formData.districtName,
            level: formData.level,
            type: formData.type
        });
    };

    return (
        <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {initialData ? "Update Institution" : "Add Institution"}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Institution Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    isInvalid={!!errors.name}
                                    placeholder="ABC Engineering College"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>State</Form.Label>
                                <Form.Select
                                    name="stateCode"
                                    value={formData.stateCode}
                                    onChange={handleChange}
                                    isInvalid={!!errors.stateCode}
                                    disabled={!!initialData}
                                >
                                    <option value="">Select State</option>
                                    {states.map((state) => (
                                        <option key={state._id} value={state.code}>
                                            {state.name} ({state.code})
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.stateCode}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>District</Form.Label>
                                <Form.Select
                                    name="districtName"
                                    value={formData.districtName}
                                    onChange={handleChange}
                                    isInvalid={!!errors.districtName}
                                    disabled={!formData.stateCode}
                                >
                                    <option value="">Select District</option>
                                    {districts.map((district) => (
                                        <option key={district._id} value={district.name}>
                                            {district.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.districtName}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Level</Form.Label>
                                <Form.Select
                                    name="level"
                                    value={formData.level}
                                    onChange={handleChange}
                                    isInvalid={!!errors.level}
                                >
                                    <option value="">Select Level</option>
                                    <option value="school">School</option>
                                    <option value="ug">UG</option>
                                    <option value="pg">PG</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.level}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Type</Form.Label>
                                <Form.Select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Type</option>
                                    <option value="Govt">Government</option>
                                    <option value="Private">Private</option>
                                    <option value="Aided">Aided</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? "Saving..." : initialData ? "Update" : "Create"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

InstitutionForm.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    loading: PropTypes.bool
};

export default InstitutionForm;
